import { RefObject, useEffect } from 'react';

/**
 * Gives every text block in a list of cards the height of the tallest one,
 * while the cards sit side by side: at least two cards, and the first card
 * has a right margin
 * @param listRef - The list holding the cards
 * @param itemSelector - Each card in the list
 * @param textSelector - Each card's text block
 * @param cardsSource - The value the list's cards are drawn from
 * @param pageRef - A block holding several lists: the cards counted and the
 * first card checked are then those of the whole block, not only this list
 */
const useEqualHeights = (
  listRef: RefObject<HTMLElement>,
  itemSelector: string,
  textSelector: string,
  cardsSource: unknown,
  pageRef?: RefObject<HTMLElement>
) => {
  useEffect(() => {
    const list = listRef.current;

    if (!list) return;

    const equalize = () => {
      const items = (pageRef?.current ?? list).querySelectorAll<HTMLElement>(
        itemSelector
      );
      const texts = Array.from(
        list.querySelectorAll<HTMLElement>(textSelector)
      );

      texts.forEach((text) => {
        text.style.minHeight = '0px';
      });

      if (
        items.length < 2 ||
        window.getComputedStyle(items[0]).marginRight === '0px'
      )
        return;

      // Whole pixels: the rendered height rounded, less padding and border
      const contentHeight = (text: HTMLElement) => {
        const style = window.getComputedStyle(text);

        return (
          text.offsetHeight -
          parseFloat(style.paddingTop) -
          parseFloat(style.paddingBottom) -
          parseFloat(style.borderTopWidth) -
          parseFloat(style.borderBottomWidth)
        );
      };
      const tallest = Math.max(...texts.map(contentHeight));

      texts.forEach((text) => {
        text.style.minHeight = `${tallest}px`;
      });
    };

    let width = window.innerWidth;

    const onResize = () => {
      if (window.innerWidth === width) return;

      width = window.innerWidth;
      equalize();
    };

    let active = true;

    equalize();
    document.fonts?.ready.then(() => {
      if (active) equalize();
    });
    window.addEventListener('load', equalize);
    window.addEventListener('resize', onResize);

    return () => {
      active = false;
      window.removeEventListener('load', equalize);
      window.removeEventListener('resize', onResize);
    };
  }, [listRef, itemSelector, textSelector, pageRef, cardsSource]);
};

export default useEqualHeights;
